// preventative-panel-visualisation.js — vanilla (matches the new Liquid IDs)

document.addEventListener("DOMContentLoaded", () => {
  /* ===============================
   * 1) RAW DATA (as provided)
   * =============================== */

  // Panel data is server-rendered by the section (single source) and read from the DOM here.
  const _ppvParse = (id) => (document.getElementById(id)?.textContent || "").trim().split("\n").filter(Boolean).map((line) => { const i = line.indexOf(","); return { gene: line.slice(0, i), condition: line.slice(i + 1) }; });
  const rawCardiac = _ppvParse("ppv-source-cardiac");
  const rawCancer = _ppvParse("ppv-source-cancer");
  const rawOther = _ppvParse("ppv-source-other");

  /* ===============================
   * 2) CATEGORY DEFINITIONS (UI)
   * =============================== */

  const categoryColors = {
    "Breast cancer": "#472A15",
    "Colorectal cancer": "#60391C",
    "Other cancer": "#7A4723",
    Cardiomyopathy: "#93562B",
    "Heart rhythm disorders": "#AC6532",
    "Other heart and circulation": "#C67439",
    "Blood and immune health": "#CD8653",
    "Brain, nerve and muscle health": "#D4976C",
    "Liver and kidney health": "#DCA985",
    "Hormones and metabolism": "#E3BB9F",
    "Skin, bones and connective tissue": "#EACDB8",
  };

  const legendCategoryOrder = [
    "Breast cancer",
    "Colorectal cancer",
    "Other cancer",
    "Cardiomyopathy",
    "Heart rhythm disorders",
    "Other heart and circulation",
    "Blood and immune health",
    "Brain, nerve and muscle health",
    "Liver and kidney health",
    "Hormones and metabolism",
    "Skin, bones and connective tissue",
  ];

  /* ===============================
   * 3) CATEGORISERS
   * =============================== */

  const cancerCategory = (cond) => {
    const s = String(cond || "").toLowerCase();
    if (s.includes("breast")) return "Breast cancer";
    if (s.includes("colorectal") || s.includes("colon")) return "Colorectal cancer";
    return "Other cancer";
  };

  const cardiacCategory = (cond) => {
    const s = String(cond || "").toLowerCase();
    if (s.includes("cardiomyopathy")) return "Cardiomyopathy";
    if (s.includes("arrhythmia") || s.includes("catecholaminergic")) return "Heart rhythm disorders";
    return "Other heart and circulation";
  };

  const otherCategory = (cond, gene) => {
    const s = String(cond || "").toLowerCase();
    const g = String(gene || "").toUpperCase();

    if (s.includes("g6pd") || s.includes("porphyria") || s.includes("fever")) {
      return "Blood and immune health";
    }

    if (
      s.includes("dystonia") ||
      s.includes("pompe") ||
      s.includes("periodic paralysis") ||
      g === "RYR1" ||
      g === "CACNA1S"
    ) {
      return "Brain, nerve and muscle health";
    }

    if (
      s.includes("alport") ||
      s.includes("polycystic kidney") ||
      s.includes("alpha-1antitrypsin") ||
      s.includes("wilson") ||
      s.includes("hemochromatosis")
    ) {
      return "Liver and kidney health";
    }

    if (
      s.includes("maturity onset diabetes") ||
      s.includes("mody") ||
      s.includes("biotinidase") ||
      s.includes("butyrylcholinesterase") ||
      s.includes("ornithine transcarbamylase")
    ) {
      return "Hormones and metabolism";
    }

    if (s.includes("cutaneous")) return "Skin, bones and connective tissue";

    return "Hormones and metabolism";
  };

  /* ===============================
   * 4) PROCESS INTO UNIFORM SHAPE
   * =============================== */

  const toCancer = ({ gene, condition }) => ({
    gene_name: gene,
    condition_name: condition,
    category: cancerCategory(condition),
  });

  const toCardiac = ({ gene, condition }) => ({
    gene_name: gene,
    condition_name: condition,
    category: cardiacCategory(condition),
  });

  const toOther = ({ gene, condition }) => ({
    gene_name: gene,
    condition_name: condition,
    category: otherCategory(condition, gene),
  });

  const cancerData = rawCancer.map(toCancer);
  const cardiacData = rawCardiac.map(toCardiac);
  const otherData = rawOther.map(toOther);

  // Preventative = union by gene across all three
  const uniqueByGene = (arr) => {
    const seen = new Set();
    return arr.filter(({ gene_name }) => {
      if (seen.has(gene_name)) return false;
      seen.add(gene_name);
      return true;
    });
  };

  const preventativeData = uniqueByGene([...cancerData, ...cardiacData, ...otherData]);

  // helper: tally categories
  const getCategoryStats = (rows) =>
    rows.reduce((acc, r) => {
      if (!acc[r.category]) acc[r.category] = { count: 0, conditions: [] };
      acc[r.category].count += 1;
      acc[r.category].conditions.push(r);
      return acc;
    }, {});

  const data = {
    cancer: {
      stats: getCategoryStats(cancerData),
      total: cancerData.length,
      title: "Cancer Panel",
      description: "hereditary cancer risk screening",
      isPreventative: false,
    },
    cardiac: {
      stats: getCategoryStats(cardiacData),
      total: cardiacData.length,
      title: "Cardiac Panel",
      description: "cardiovascular and heart condition screening",
      isPreventative: false,
    },
    preventative: {
      stats: getCategoryStats(preventativeData),
      total: 184, // hard cap per spec
      title: "Preventative Comprehensive",
      description: "across health impact categories",
      isPreventative: true,
    },
  };

  // Flat, table-view data (one row per gene) keyed by panel
  const flatData = {
    cancer: cancerData,
    cardiac: cardiacData,
    preventative: preventativeData,
  };

  /* ===============================
   * 5) DOM REFS (match Liquid)
   * =============================== */

  const ppvContainer = document.querySelector("[data-ppv-root]");
  const ppvGeneLinks = new Set((ppvContainer?.dataset.ppvGeneLinks || "").split("|").filter(Boolean));
  const ppvGeneUrl = ppvContainer?.dataset.ppvGeneUrl || "";
  const geneHtml = (g) => {
    const sym = String(g == null ? "" : g);
    if (!ppvGeneLinks.has(sym)) return escapeHtml(sym);
    return `<a class="ppv-gene-link" href="${escapeHtml(ppvGeneUrl + encodeURIComponent(sym.toLowerCase()))}">${escapeHtml(sym)}</a>`;
  };

  const enabledPanels = {
    cancer: ppvContainer?.dataset.ppvShowCancer === "true",
    cardiac: ppvContainer?.dataset.ppvShowCardiac === "true",
    preventative: ppvContainer?.dataset.ppvShowPreventative === "true",
  };

  // Download button follows the active tab: Cancer / Heart health / full preventative list.
  const ppvPdfBtn = document.querySelector(".ppv-pdf-download");
  const ppvPdfBtnLabel = ppvPdfBtn?.querySelector("span");
  const ppvPdfUrls = {
    cancer: ppvContainer?.dataset.ppvPdfCancer,
    cardiac: ppvContainer?.dataset.ppvPdfCardiac,
    preventative: ppvContainer?.dataset.ppvPdfPreventative,
  };
  const ppvPdfNames = {
    cancer: "eugene-cancer-risk-gene-list.pdf",
    cardiac: "eugene-heart-health-gene-list.pdf",
    preventative: "eugene-preventative-gene-list.pdf",
  };
  const ppvPdfLabels = {
    cancer: "Download Cancer gene list",
    cardiac: "Download Heart health gene list",
    preventative: "Download full gene list",
  };
  function updatePpvPdfDownload() {
    if (!ppvPdfBtn) return;
    const url = ppvPdfUrls[state.activeTab];
    if (url) {
      ppvPdfBtn.href = url;
      ppvPdfBtn.setAttribute("download", ppvPdfNames[state.activeTab] || "gene-list.pdf");
      if (ppvPdfBtnLabel) ppvPdfBtnLabel.textContent = ppvPdfLabels[state.activeTab] || "Download gene list";
    }
  }

  const enabledCount = Object.values(enabledPanels).filter(Boolean).length;

  const searchEnabled = ppvContainer?.dataset.ppvShowSearch === "true";
  const tabsEnabled = ppvContainer?.dataset.ppvShowTabs !== "false"; // default true

  const viewToggleEnabled = ppvContainer?.dataset.ppvShowViewToggle === "true";
  const defaultView = ppvContainer?.dataset.ppvDefaultView === "category" ? "category" : "table";
  // Default true; set false on dedicated full-list pages to show every row (no teaser).
  const tableCollapsible = ppvContainer?.dataset.ppvTableCollapsible !== "false";

  const initialTabFromLiquid = ppvContainer?.dataset.ppvInitialTab;

  /* ===============================
   * 6) DOM REFS (match Liquid)
   * =============================== */

  const searchInput = document.getElementById("ppv-search-input");
  const noResultsEl = document.getElementById("ppv-no-results");
  const legendContainer = document.getElementById("ppv-legend");
  const tabsWrapper = document.querySelector(".ppv-tabs-nav-wrapper");
  const tabsNavContainer = document.querySelector(".ppv-tabs-nav-container");

  const tabButtons = {
    cancer: document.getElementById("ppv-tab-btn-cancer"),
    cardiac: document.getElementById("ppv-tab-btn-cardiac"),
    preventative: document.getElementById("ppv-tab-btn-preventative"),
  };

  const tabContents = {
    cancer: document.getElementById("ppv-tab-content-cancer"),
    cardiac: document.getElementById("ppv-tab-content-cardiac"),
    preventative: document.getElementById("ppv-tab-content-preventative"),
  };

  const categoryContainers = {
    cancer: document.getElementById("ppv-categories-container-cancer"),
    cardiac: document.getElementById("ppv-categories-container-cardiac"),
    preventative: document.getElementById("ppv-categories-container-preventative"),
  };

  const tableContainers = {
    cancer: document.getElementById("ppv-table-container-cancer"),
    cardiac: document.getElementById("ppv-table-container-cardiac"),
    preventative: document.getElementById("ppv-table-container-preventative"),
  };

  const viewControls = document.querySelector(".ppv-view-controls");
  const viewCountEl = document.getElementById("ppv-view-count");
  const viewButtons = viewControls ? Array.from(viewControls.querySelectorAll(".ppv-view-btn")) : [];

  /* ===============================
   * 7) STATE
   * =============================== */

  const state = {
    activeTab: "cancer",
    searchTerm: "",
    viewMode: defaultView, // 'table' | 'category'
    tableExpanded: false,
    openCategories: {
      cancer: new Set(),
      cardiac: new Set(),
      preventative: new Set(),
    },
  };

  const isMobile = () => window.matchMedia("(max-width: 767px)").matches;

  /* ===============================
   * 8) STATE INIT (from Liquid)
   * =============================== */

  const fallbackFirstEnabled = () => {
    if (enabledPanels.preventative) return "preventative";
    if (enabledPanels.cancer) return "cancer";
    if (enabledPanels.cardiac) return "cardiac";
    return "preventative";
  };

  const isTabEnabled = (tab) => Boolean(enabledPanels[tab]);

  state.activeTab = isTabEnabled(initialTabFromLiquid) ? initialTabFromLiquid : fallbackFirstEnabled();

// Only force the tabs visible when tabs are enabled; honour show_tabs=false
// (e.g. single-panel conditions pages) so the lone tab stays hidden.
if (tabsWrapper && tabsEnabled) {
  tabsWrapper.style.removeProperty("display");
  tabsWrapper.hidden = false;
  tabsWrapper.setAttribute("aria-hidden", "false");
}
  // Hide disabled panels' DOM nodes (defensive, in case Liquid didn't)
  Object.keys(enabledPanels).forEach((panel) => {
    if (enabledPanels[panel]) return;
    tabButtons[panel]?.setAttribute("hidden", "");
    tabButtons[panel]?.style.setProperty("display", "none");
    tabContents[panel]?.setAttribute("hidden", "");
    tabContents[panel]?.style.setProperty("display", "none");
  });

  // Search disabled => hide no results messaging too
  if (!searchEnabled) {
    searchInput?.setAttribute("hidden", "");
    if (noResultsEl) noResultsEl.hidden = true;
  }

  /* ===============================
   * 9) RENDER: LEGEND + CHARTS + LISTS
   * =============================== */

  function renderLegend() {
    if (!legendContainer) return;
    legendContainer.innerHTML = legendCategoryOrder
      .map(
        (cat) => `
        <div class="ppv-legend-item" data-category="${cat}">
          <div class="ppv-legend-color-box" data-category="${cat}" style="background-color:${categoryColors[cat]}">&nbsp;</div>
          <span class="ppv-legend-text">${cat}</span>
        </div>
      `
      )
      .join("");
  }

  function updateAllCounts() {
    ["cancer", "cardiac", "preventative"].forEach((panel) => {
      const span = document.getElementById(`ppv-count-${panel}`);
      if (span) span.textContent = data[panel].total;
      const tabCount = document.querySelector(`.ppv-tab-count[data-panel="${panel}"]`);
      if (tabCount) tabCount.textContent = data[panel].total;
    });
  }

  function renderCharts() {
    ["cancer", "cardiac", "preventative"].forEach((panel) => renderChart(panel, data[panel]));
  }

  function renderChart(panel, panelData) {
    const { stats, total, isPreventative } = panelData;
    const container = document.getElementById(`ppv-chart-bar-${panel}`);
    if (!container) return;

    const MAX = 184;
    const displayTotal = isPreventative ? 184 : total;

    if (!displayTotal) {
      container.innerHTML = "";
      return;
    }

    const order = legendCategoryOrder.filter((cat) => stats[cat] && stats[cat].count > 0);
    const barWidthPct = (displayTotal / MAX) * 100 * 0.8;

    const segs = order
      .map((cat) => {
        const pct = (stats[cat].count / displayTotal) * 100;
        return `<div class="ppv-chart-segment"
          style="background-color:${categoryColors[cat]};width:${pct}%"
          data-category="${cat}" data-count="${stats[cat].count}" data-pct="${pct}">&nbsp;</div>`;
      })
      .join("");

    container.innerHTML = `<div class="ppv-chart-bar" data-totalpct="${barWidthPct}" style="width:${barWidthPct}%">${segs}</div>`;
  }

  function setActiveTab(tab) {
    if (!isTabEnabled(tab)) return;
    state.activeTab = tab;
    state.tableExpanded = false;
    renderTabs();
    updatePpvPdfDownload();
    if (activeCategory) setCategoryHighlight(activeCategory);
  }

  function renderTabs() {
    Object.keys(tabContents).forEach((tab) => {
      if (!enabledPanels[tab]) return;
      tabContents[tab]?.classList.toggle("active", state.activeTab === tab);
      tabButtons[tab]?.classList.toggle("active", state.activeTab === tab);
    });

    // ensure disabled tabs are not active
    Object.keys(enabledPanels).forEach((tab) => {
      if (enabledPanels[tab]) return;
      tabButtons[tab]?.classList.remove("active");
      tabContents[tab]?.classList.remove("active");
    });

    renderActiveView();
  }

  function renderActiveTabContent() {
    const panel = state.activeTab;
    const { stats } = data[panel];
    const openSet = state.openCategories[panel];
    const container = categoryContainers[panel];
    if (!container) return;

    const q = state.searchTerm.trim().toLowerCase();

    const filteredCats = legendCategoryOrder.filter((cat) => {
      const st = stats[cat];
      if (!st || st.count === 0) return false;
      if (!q) return true;
      return st.conditions.some(
        (c) =>
          String(c.condition_name || "").toLowerCase().includes(q) ||
          String(c.gene_name || "").toLowerCase().includes(q)
      );
    });

    if (filteredCats.length === 0 && q) {
      container.innerHTML = "";
      if (noResultsEl) noResultsEl.style.display = "block";
      return;
    }
    if (noResultsEl) noResultsEl.style.display = "none";

    container.innerHTML = filteredCats
      .map((cat) => {
        const st = stats[cat];
        const isOpen = openSet.has(cat);
        return renderCategorySection(cat, st, isOpen);
      })
      .join("");
  }

  function renderCategorySection(category, stat, isOpen) {
    const q = state.searchTerm.trim().toLowerCase();

    const base = !q
      ? stat.conditions
      : stat.conditions.filter(
          (c) =>
            String(c.condition_name || "").toLowerCase().includes(q) ||
            String(c.gene_name || "").toLowerCase().includes(q)
        );

    const displayCount = q ? base.length : stat.count;

    const itemsHtml = (q ? base : base.slice(0, 50))
      .map(
        (c) => `
        <div class="ppv-condition-item">
          <div class="ppv-condition-info-left">
            <h4 class="ppv-condition-name">${c.condition_name}</h4>
            <div class="ppv-condition-details">
              <span>Gene: <span class="ppv-condition-gene">${geneHtml(c.gene_name)}</span></span>
            </div>
          </div>
        </div>
      `
      )
      .join("");

    const moreHtml =
      !q && stat.conditions.length > 50
        ? `<div class="ppv-conditions-more">... and ${stat.conditions.length - 50} more genes</div>`
        : "";

    return `
      <div class="ppv-category-card">
        <button type="button" class="ppv-category-card-header" data-category="${category}" aria-expanded="${isOpen}">
          <div class="ppv-header-top-row">
            <div class="ppv-category-color-box no-empty" style="background-color:${categoryColors[category]}"></div>
            <div class="ppv-header-main-content">
              <h3 class="ppv-category-title">${category}</h3>
              <p class="ppv-category-description"></p>
            </div>
          </div>
          <div class="ppv-header-right-items">
            <span class="ppv-category-badge">${displayCount} gene${displayCount !== 1 ? "s" : ""}</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ppv-chevron"><polyline points="6 9 12 15 18 9"></polyline></svg>
          </div>
        </button>
        <div class="ppv-category-card-content ${isOpen ? "open" : ""}">${itemsHtml}${moreHtml}</div>
      </div>
    `;
  }

  /* ===============================
   * 9b) RENDER: TABLE VIEW + VIEW SWITCH
   * =============================== */

  function escapeHtml(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  // Filtered + alphabetised rows for the active panel's table view
  function getTableRows(panel) {
    const q = state.searchTerm.trim().toLowerCase();
    const rows = (flatData[panel] || []).filter((r) => {
      if (!q) return true;
      return (
        String(r.condition_name || "").toLowerCase().includes(q) ||
        String(r.gene_name || "").toLowerCase().includes(q)
      );
    });
    return rows.slice().sort((a, b) =>
      String(a.gene_name).localeCompare(String(b.gene_name))
    );
  }

  function renderTable(panel) {
    const container = tableContainers[panel];
    if (!container) return;

    const q = state.searchTerm.trim().toLowerCase();
    const rows = getTableRows(panel);

    if (rows.length === 0) {
      container.innerHTML = "";
      if (noResultsEl) noResultsEl.style.display = q ? "block" : "none";
      return;
    }
    if (noResultsEl) noResultsEl.style.display = "none";

    // Collapse to a teaser only when not searching (and only if enabled — full-list pages opt out)
    const collapsible = tableCollapsible && !q && rows.length > 6;
    const isCollapsed = collapsible && !state.tableExpanded;

    const body = rows
      .map(
        (r) => `
        <tr>
          <td class="ppv-cell-gene">${geneHtml(r.gene_name)}</td>
          <td class="ppv-cell-condition">${escapeHtml(r.condition_name)}</td>
          <td class="ppv-cell-category">
            <span class="ppv-cell-category-inner">
              <span class="ppv-cat-swatch" style="background-color:${categoryColors[r.category] || "#ccc"}"></span>
              ${escapeHtml(r.category)}
            </span>
          </td>
        </tr>`
      )
      .join("");

    const chevron =
      '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ppv-chevron"><polyline points="6 9 12 15 18 9"></polyline></svg>';

    const fullBtn = collapsible
      ? `<button type="button" class="ppv-show-full" aria-expanded="${state.tableExpanded}">${
          state.tableExpanded ? "Show less" : "Show full " + rows.length + " gene list"
        } ${chevron}</button>`
      : "";

    container.innerHTML = `
      <div class="ppv-table-wrap ${isCollapsed ? "is-collapsed" : ""}">
        <table class="ppv-gene-table">
          <thead><tr><th>Gene</th><th>Condition</th><th>Category</th></tr></thead>
          <tbody>${body}</tbody>
        </table>
      </div>
      ${fullBtn}
    `;
  }

  function updateViewControls() {
    const panel = state.activeTab;
    viewButtons.forEach((btn) =>
      btn.classList.toggle("active", btn.dataset.view === state.viewMode)
    );
    if (!viewCountEl) return;
    const q = state.searchTerm.trim().toLowerCase();
    let n;
    if (q) n = getTableRows(panel).length;
    else n = state.viewMode === "table" ? getTableRows(panel).length : (data[panel] ? data[panel].total : 0);
    const suffix = state.viewMode === "table" ? " alphabetical" : "";
    viewCountEl.textContent = `${n} gene${n === 1 ? "" : "s"}${suffix}`;
  }

  // Dispatcher: show the right container for the active view + render it
  function renderActiveView() {
    const isTable = state.viewMode === "table";
    Object.keys(tabContents).forEach((p) => {
      // category container's CSS default is visible (flex); restore with "".
      // table container's CSS default is none, so it must be shown explicitly.
      if (categoryContainers[p]) categoryContainers[p].style.display = isTable ? "none" : "";
      if (tableContainers[p]) tableContainers[p].style.display = isTable ? "block" : "none";
    });

    if (isTable) renderTable(state.activeTab);
    else renderActiveTabContent();

    updateViewControls();
  }

  /* ===============================
   * 10) INTERACTIONS (hover / tap / legend)
   * =============================== */

  const ppvRoot = document.querySelector(".ppv-section-wrapper") || document;
  const chartsRoot = document.querySelector(".ppv-all-charts-container");
  let activeCategory = null;

  function dimAllExcept(category) {
    const segs = ppvRoot.querySelectorAll(".ppv-chart-segment");
    segs.forEach((seg) => {
      const match = seg.dataset.category === category;
      seg.style.opacity = match ? "1" : "0.35";
      seg.classList.toggle("is-active", match);
      seg.classList.toggle("segment-hovered", match);
    });
  }

  function clearDim() {
    const segs = ppvRoot.querySelectorAll(".ppv-chart-segment");
    segs.forEach((seg) => {
      seg.style.opacity = "";
      seg.classList.remove("is-active", "segment-hovered");
    });
    ppvRoot.querySelectorAll(".ppv-chart-tooltip.visible").forEach((t) => t.classList.remove("visible"));
  }

  function setActiveCategory(next) {
    clearCategoryHighlight();
    legendContainer?.querySelectorAll(".ppv-legend-item").forEach((i) => i.classList.remove("active"));
    activeCategory = next;
    if (!next) return;

    legendContainer
      ?.querySelector(`.ppv-legend-item[data-category="${CSS.escape(next)}"]`)
      ?.classList.add("active");

    setCategoryHighlight(next);
  }

  function clearCategoryHighlight() {
    ppvRoot.classList.remove("is-highlighting");
    clearDim();
  }

  function setCategoryHighlight(category) {
    ppvRoot.classList.add("is-highlighting");
    dimAllExcept(category);

    ppvRoot.querySelectorAll(".ppv-chart-wrapper").forEach((wrapper) => {
      const seg = wrapper.querySelector(`.ppv-chart-segment[data-category="${CSS.escape(category)}"]`);
      const bar = wrapper.querySelector(".ppv-chart-bar");
      if (!bar) return;

      let tooltip = wrapper.querySelector(".ppv-chart-tooltip");
      if (!tooltip) {
        tooltip = document.createElement("div");
        tooltip.className = "ppv-chart-tooltip";
        wrapper.appendChild(tooltip);
      }

      const wrapRect = wrapper.getBoundingClientRect();
      let left;

      if (seg) {
        const segRect = seg.getBoundingClientRect();
        left = segRect.left - wrapRect.left;
        const count = seg.dataset.count;

        tooltip.innerHTML = `
          <p class="ppv-tooltip-category">${category}</p>
          <p class="ppv-tooltip-count">${count} gene${count === "1" ? "" : "s"}</p>
        `;
        tooltip.classList.remove("ppv-tooltip-empty");
      } else {
        const barRect = bar.getBoundingClientRect();
        left = barRect.right - wrapRect.left - 1;

        tooltip.innerHTML = `
          <p class="ppv-tooltip-category">${category}</p>
          <p class="ppv-tooltip-count">Not included</p>
        `;
        tooltip.classList.add("ppv-tooltip-empty");
      }

      if (isMobile()) {
        const wrapW = wrapRect.width;
        tooltip.style.whiteSpace = "normal";
        tooltip.style.maxWidth = Math.max(0, wrapW - 16) + "px";

        requestAnimationFrame(() => {
          const ttW = tooltip.offsetWidth || 0;
          const clamped = Math.min(Math.max(left, 8), Math.max(8, wrapW - ttW - 8));
          tooltip.style.left = clamped + "px";
          tooltip.style.transform = "translateX(0)";
          tooltip.classList.add("visible");
        });
      } else {
        tooltip.style.removeProperty("max-width");
        tooltip.style.whiteSpace = "";
        tooltip.style.left = left + "px";
        tooltip.style.transform = "translateX(0)";
        tooltip.classList.add("visible");
      }
    });
  }

  function handleAboutToggle(e) {
    const trigger = e.target.closest(".ppv-collapsible-trigger");
    if (!trigger) return;
    const content = trigger.nextElementSibling;
    const isExpanded = trigger.getAttribute("aria-expanded") === "true";
    trigger.setAttribute("aria-expanded", String(!isExpanded));
    content?.classList.toggle("open");
  }

  function handleTabClick(e) {
    const tab = e.target.closest(".ppv-tab-button")?.dataset.tab;
    if (tab && tab !== state.activeTab) setActiveTab(tab);
  }

  function handleSearchInput(e) {
    state.searchTerm = e.target.value;
    renderActiveView();
    if (activeCategory) setCategoryHighlight(activeCategory);
  }

  function handleCategoryToggle(e) {
    const header = e.target.closest(".ppv-category-card-header");
    if (!header) return;

    const category = header.dataset.category;
    const openSet = state.openCategories[state.activeTab];

    if (openSet.has(category)) openSet.delete(category);
    else openSet.add(category);

    renderActiveTabContent();
    if (activeCategory) setCategoryHighlight(activeCategory);
  }

  function handleChartHover(e) {
    if (isMobile() || activeCategory) return;
    const seg = e.target.closest(".ppv-chart-segment");
    if (!seg) return;
    const cat = seg.dataset.category;

    setCategoryHighlight(cat);

    legendContainer?.querySelectorAll(".ppv-legend-item").forEach((i) =>
      i.classList.toggle("hover", i.dataset.category === cat)
    );
  }

  function handleChartMouseOut(e) {
    const leavingSeg = e.target.closest(".ppv-chart-segment");
    if (!leavingSeg) return;

    legendContainer?.querySelectorAll(".ppv-legend-item.hover").forEach((i) => i.classList.remove("hover"));
    if (!activeCategory) clearCategoryHighlight();
  }

  function handleChartTap(e) {
    if (!isMobile()) return;
    const seg = e.target.closest(".ppv-chart-segment");
    if (!seg) return;
    const cat = seg.dataset.category;
    setActiveCategory(activeCategory === cat ? null : cat);
  }

  function handleLegendClick(e) {
    const item = e.target.closest(".ppv-legend-item");
    if (!item) return;
    const cat = item.dataset.category;
    setActiveCategory(activeCategory === cat ? null : cat);
  }

  function handleLegendHover(e) {
    if (isMobile() || activeCategory) return;
    const item = e.target.closest(".ppv-legend-item");
    if (!item) return;
    setCategoryHighlight(item.dataset.category);
  }

  function handleLegendOut() {
    if (!activeCategory) clearCategoryHighlight();
  }

  /* ===============================
   * 11) LIFECYCLE
   * =============================== */

  function renderAll() {
    renderLegend();
    renderCharts();
    updateAllCounts();
    renderTabs();
  }

  // View toggle disabled => hide the controls row entirely
  if (!viewToggleEnabled && viewControls) {
    viewControls.setAttribute("hidden", "");
    viewControls.style.display = "none";
  }

  function handleViewToggle(e) {
    const btn = e.target.closest(".ppv-view-btn");
    if (!btn) return;
    const view = btn.dataset.view;
    if (!view || view === state.viewMode) return;
    state.viewMode = view;
    state.tableExpanded = false;
    renderActiveView();
  }

  function handleShowFull(e) {
    const btn = e.target.closest(".ppv-show-full");
    if (!btn) return;
    state.tableExpanded = !state.tableExpanded;
    renderActiveView();
  }

  // listeners
  tabsNavContainer?.addEventListener("click", handleTabClick);
  searchInput?.addEventListener("input", handleSearchInput);
  viewControls?.addEventListener("click", handleViewToggle);
  document.querySelector(".ppv-tabs-content-wrapper")?.addEventListener("click", handleCategoryToggle);
  document.querySelector(".ppv-tabs-content-wrapper")?.addEventListener("click", handleShowFull);
  document.querySelector(".ppv-about-container")?.addEventListener("click", handleAboutToggle);
  chartsRoot?.addEventListener("mouseover", handleChartHover);
  chartsRoot?.addEventListener("mouseout", handleChartMouseOut);
  chartsRoot?.addEventListener("click", handleChartTap);
  legendContainer?.addEventListener("click", handleLegendClick);
  legendContainer?.addEventListener("mouseover", handleLegendHover);
  legendContainer?.addEventListener("mouseout", handleLegendOut);

  // clicking outside charts/legend clears pin
  document.addEventListener("click", (e) => {
    const inLegend = e.target.closest("#ppv-legend");
    const inCharts = e.target.closest(".ppv-all-charts-container");
    if (!inLegend && !inCharts && ppvRoot.contains(e.target)) setActiveCategory(null);
  });

  // go
  renderAll();
  updatePpvPdfDownload();
});